import React, { useState, useEffect, useRef } from 'react';
import { walletApi } from '../services/walletApi';
import { useToken } from '../contexts/TokenContext';

const WalletGroupSelector = ({ onSelectWallet, onSelectGroup, selectedWallet, selectedGroupProp }) => {
  const { tokenId } = useToken();
  const [isOpen, setIsOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorState, setErrorState] = useState(null);
  const [selectedGroupInternal, setSelectedGroupInternal] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  
  // Sync internal state with props
  useEffect(() => {
    if (selectedGroupProp) {
      setSelectedGroupInternal(selectedGroupProp);
    }
  }, [selectedGroupProp]);
  
  // Format wallet address to show first 4 and last 4 characters
  const formatAddress = (address) => {
    if (!address || address.length < 10) return address;
    return `${address.substring(0, 4)}...${address.substring(address.length - 4)}`;
  };
  
  // Initial load and reload when tokenId changes
  useEffect(() => {
    fetchData();
  }, [tokenId]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        checkDropdownPosition();
      }
    };

    const handleResize = () => {
      if (isOpen) {
        checkDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  // Check dropdown position when opened
  useEffect(() => {
    if (isOpen) {
      checkDropdownPosition();
    }
  }, [isOpen]);
  
  // Check if dropdown should open upward or downward
  const checkDropdownPosition = () => {
    if (!buttonRef.current) return;
    
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const dropdownHeight = 400; // Approximate height of dropdown
    
    // If there's not enough space below and more space above, open upward
    if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
      setDropdownPosition('top');
    } else {
      setDropdownPosition('bottom');
    }
  };
  
  // Fetch groups and wallets
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get wallets and groups filtered by token
      const response = await walletApi.getUserWallets(null, tokenId);
      const groupsResponse = await walletApi.getWalletGroups(tokenId);
      
      setWallets(response.wallets || []);
      setGroups(groupsResponse || []);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching wallet groups:', error);
      setErrorState('Failed to load wallets.');
      setLoading(false);
    }
  };
  
  // Handle group selection
  const handleGroupSelect = async (group) => {
    try {
      setLoading(true);
      setSelectedGroupInternal(group);
      
      // Call onSelectGroup prop if it exists
      if (onSelectGroup) {
        onSelectGroup(group);
      }
      
      setLoading(false);
      setIsOpen(false);
    } catch (error) {
      console.error('Error selecting group:', error);
      setErrorState('Failed to select group.');
      setLoading(false);
    }
  };
  
  // Handle wallet selection
  const handleWalletSelect = (wallet) => {
    // Call onSelectWallet prop if it exists
    if (onSelectWallet) {
      onSelectWallet(wallet);
    }
    
    // Clear group selection if we're selecting a wallet
    setSelectedGroupInternal(null);
    if (onSelectGroup) {
      onSelectGroup(null);
    }
    
    setIsOpen(false);
  };
  
  // Filter wallets by search query
  const filteredWallets = wallets.filter(wallet => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      (wallet.name && wallet.name.toLowerCase().includes(query)) ||
      wallet.address.toLowerCase().includes(query)
    );
  });
  
  // Copy address to clipboard
  const copyToClipboard = (e, address) => {
    e.stopPropagation();
    
    navigator.clipboard.writeText(address)
      .then(() => {
        const tempEl = document.createElement('div');
        tempEl.className = 'fixed bottom-4 right-4 bg-green-600 text-white px-4 py-2 rounded shadow-lg text-sm z-50 animate-pulse';
        tempEl.textContent = 'Address copied to clipboard!';
        document.body.appendChild(tempEl);
        setTimeout(() => {
          if (document.body.contains(tempEl)) {
            document.body.removeChild(tempEl);
          }
        }, 2000);
      })
      .catch(err => {
        console.error('Failed to copy address:', err);
      });
  };

  // Get dropdown classes based on position
  const getDropdownClasses = () => {
    const baseClasses = "absolute z-50 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl transition-colors";
    
    if (dropdownPosition === 'top') {
      return `${baseClasses} bottom-full mb-1 max-h-96 overflow-hidden`;
    } else {
      return `${baseClasses} top-full mt-1 max-h-96 overflow-hidden`;
    }
  };
  
  // What to display in the button
  const getSelectedDisplay = () => {
    if (selectedGroupInternal) {
      return (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center min-w-0 flex-1">
            <div 
              className="w-3 h-3 rounded-full mr-2" 
              style={{ backgroundColor: selectedGroupInternal.color || '#9333EA' }}
            ></div>
            <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
              Group: {selectedGroupInternal.name}
            </span>
            <span className="ml-2 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-2 py-0.5 rounded-full">
              {selectedGroupInternal.wallet_count || 0} wallets
            </span>
          </div>
          <svg 
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 ${
              isOpen ? 'transform rotate-180' : ''
            }`} 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      );
    } else if (selectedWallet) {
      return (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center min-w-0 flex-1">
            <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
              {selectedWallet.name || 'Unnamed Wallet'}
            </span>
            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400 flex-shrink-0">
              ({formatAddress(selectedWallet.address)})
            </span>
          </div>
          <svg 
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ml-2 ${
              isOpen ? 'transform rotate-180' : ''
            }`} 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      );
    } else {
      return (
        <div className="flex justify-between w-full items-center">
          <span className="text-gray-500 dark:text-gray-400">Select a wallet or group</span>
          <svg 
            className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
              isOpen ? 'transform rotate-180' : ''
            }`} 
            xmlns="http://www.w3.org/2000/svg" 
            viewBox="0 0 20 20" 
            fill="currentColor"
          >
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }
  };
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={() => {
          setIsOpen(!isOpen);
          if (!isOpen) {
            // Small delay to ensure the dropdown is rendered before checking position
            setTimeout(checkDropdownPosition, 50);
          }
        }}
        className="w-full flex items-center justify-between px-4 py-3 text-left bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
        data-wallet-address={selectedWallet?.address || ''}
      >
        {getSelectedDisplay()}
      </button>

      {isOpen && (
        <div className={getDropdownClasses()}>
          {/* Search field */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-lg transition-colors">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search wallets..."
              className="w-full p-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
              autoFocus
            />
          </div>
          
          <div className="flex" style={{ height: '320px' }}>
            {/* Group selector column */}
            <div className="w-1/3 border-r border-gray-200 dark:border-gray-600 overflow-y-auto bg-gray-50 dark:bg-gray-700 transition-colors">
              <button
                onClick={() => handleWalletSelect(null)}
                className={`w-full p-3 text-left text-sm hover:bg-white dark:hover:bg-gray-600 transition-colors ${
                  !selectedWallet && !selectedGroupInternal ? 'bg-white dark:bg-gray-600 font-medium text-purple-700 dark:text-purple-300 border-r-2 border-purple-500' : 'text-gray-700 dark:text-gray-300'
                }`}
              >
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-gray-400 rounded-full mr-2"></div>
                  Individual Wallets
                </div>
              </button>
              
              {/* Group list */}
              {groups.length > 0 && (
                <div className="border-t border-gray-300 dark:border-gray-600 mt-1 pt-1">
                  <div className="px-3 py-1 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                    Wallet Groups
                  </div>
                  {groups.map(group => (
                    <button
                      key={group.id}
                      onClick={() => handleGroupSelect(group)}
                      className={`w-full p-3 text-left text-sm hover:bg-white dark:hover:bg-gray-600 transition-colors ${
                        selectedGroupInternal?.id === group.id ? 'bg-white dark:bg-gray-600 font-medium text-purple-700 dark:text-purple-300 border-r-2 border-purple-500' : 'text-gray-700 dark:text-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center min-w-0 flex-1">
                          <div 
                            className="w-2 h-2 rounded-full mr-2 flex-shrink-0" 
                            style={{ backgroundColor: group.color || '#9333EA' }}
                          ></div>
                          <span className="truncate">{group.name}</span>
                        </div>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1 flex-shrink-0">
                          ({group.wallet_count || 0})
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Wallet list column */}
            <div className="flex-1 flex flex-col bg-white dark:bg-gray-800 transition-colors">
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="flex justify-center items-center h-full">
                    <div className="w-6 h-6 border-2 border-gray-300 border-t-purple-600 rounded-full animate-spin"></div>
                  </div>
                ) : filteredWallets.length > 0 ? (
                  <div>
                    {filteredWallets.map(wallet => (
                      <div
                        key={wallet.id}
                        onClick={() => handleWalletSelect(wallet)}
                        className={`p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 transition-colors ${
                          selectedWallet?.id === wallet.id && !selectedGroupInternal ? 'bg-purple-50 dark:bg-purple-900/30 border-purple-100 dark:border-purple-600' : ''
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center">
                              <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                {wallet.name || 'Unnamed Wallet'}
                              </span>
                              {wallet.is_default && (
                                <span className="ml-2 text-xs bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 px-1.5 py-0.5 rounded-full flex-shrink-0">
                                  Default
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 font-mono">
                              {formatAddress(wallet.address)}
                            </div>
                          </div>
                          <button
                            onClick={(e) => copyToClipboard(e, wallet.address)}
                            className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors flex-shrink-0"
                            title="Copy address"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                              <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-6 text-center text-gray-500 dark:text-gray-400">
                    <div className="text-gray-400 dark:text-gray-500 mb-2">
                      <svg className="w-8 h-8 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    {searchQuery ? 'No wallets match your search' : 'No wallets found'}
                  </div>
                )}
              </div>
              
              {/* Manage wallets link - sticky at bottom */}
              <div className="border-t border-gray-200 dark:border-gray-600 p-3 bg-gray-50 dark:bg-gray-700 rounded-b-lg transition-colors">
                <a 
                  href="/wallets" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block w-full py-2.5 px-4 text-center text-sm bg-purple-600 text-white rounded-md hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 transition-colors font-medium"
                >
                  Manage Wallets & Groups
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletGroupSelector;